import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Features from './components/Features';
import Benefits from './components/Benefits';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import Signup from './components/Signup';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaWebSite = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '스마트스코어 멤버십',
  url: SITE_URL,
  description: '대한민국 골퍼 500만의 선택 스마트스코어 멤버십. 전국 420여 개 골프장 제휴, 입회비 100% 반환, 5년간 연회비 면제.',
  inLanguage: 'ko-KR',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const schemaOrganization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '스마트스코어',
  url: SITE_URL,
  logo: 'https://static.readdy.ai/image/135a6647eea71ccd89950e580a6d6dcb/07cfc35c21842a86ffee7c15fc8d7e6b.png',
  description: '대한민국 골퍼 500만의 선택. 전국 420여 개 골프장 제휴 프리미엄 골프 멤버십 서비스.',
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    availableLanguage: 'Korean',
  },
};

const schemaService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: '스마트스코어 골프 멤버십',
  provider: {
    '@type': 'Organization',
    name: '스마트스코어',
    url: SITE_URL,
  },
  serviceType: '골프 멤버십',
  description: '전국 420여 개 골프장 제휴, 입회비 100% 반환, 5년간 연회비 면제 혜택을 제공하는 프리미엄 골프 멤버십.',
  areaServed: {
    '@type': 'Country',
    name: '대한민국',
  },
  offers: [
    {
      '@type': 'Offer',
      name: '개인 멤버십',
      price: '33000000',
      priceCurrency: 'KRW',
      description: '기명 1인, 주중 6회 / 주말 24회, 그린피 50% 할인, 5년 후 100% 반환',
    },
    {
      '@type': 'Offer',
      name: '개인 1+1 멤버십',
      price: '63000000',
      priceCurrency: 'KRW',
      description: '기명 1인 + 무기명 1인, 주중 6회 / 주말 24회, 그린피 50% 할인, 5년 후 100% 반환',
    },
    {
      '@type': 'Offer',
      name: '법인 멤버십',
      price: '129000000',
      priceCurrency: 'KRW',
      description: '무기명 4인, 주중 6회 / 주말 24회, 그린피 50% 할인, 5년 후 100% 반환',
    },
    {
      '@type': 'Offer',
      name: '법인 고급형 멤버십',
      price: '285000000',
      priceCurrency: 'KRW',
      description: '무기명 4인, 주중 6회 / 주말 24회, 그린피 50% 할인, 5년 후 100% 반환',
    },
    {
      '@type': 'Offer',
      name: '법인 프리미엄 멤버십',
      price: '519000000',
      priceCurrency: 'KRW',
      description: '무기명 4인, 주중 12회 / 주말 36회, 그린피 전액 무료, 5년 후 100% 반환',
    },
  ],
};

const schemaFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '입회비는 정말 100% 반환되나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '네, 5년 만기 후 입회비 전액을 반환해 드립니다. 스마트스코어는 중소기업 기업부 예비 유니콘으로 선정된 안정적인 기업으로, NH투자증권 PE, 산업은행 등 검증된 투자자들이 참여하고 있어 안심하고 이용하실 수 있습니다.',
      },
    },
    {
      '@type': 'Question',
      name: '연회비 5년간 면제 혜택은 누구에게 적용되나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '현재 진행 중인 동계 프로모션 기간에 가입하시는 모든 회원님께 적용됩니다. 선착순 50구좌 한정이며, 상품에 따라 최대 5,000만원의 혜택을 받으실 수 있습니다.',
      },
    },
    {
      '@type': 'Question',
      name: '무기명 멤버십은 어떻게 사용하나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '무기명 멤버십은 본인 외에도 가족, 지인, 비즈니스 파트너 등 누구나 사용할 수 있습니다. 법인 상품의 경우 4인 무기명으로 제공되어 접대나 임직원 복지용으로 활용하기에 최적입니다.',
      },
    },
    {
      '@type': 'Question',
      name: '전국 420여 개 골프장은 어디서 확인할 수 있나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '상담 신청 시 전국 제휴 골프장 리스트와 상세 정보를 제공해 드립니다. 수도권, 제주를 포함한 전국 명문 골프장들이 포함되어 있으며, 해외 골프장도 이용 가능합니다.',
      },
    },
    {
      '@type': 'Question',
      name: '예약은 어떻게 하나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '1:1 전담 컨시어지를 통해 예약하실 수 있습니다. 스마트스코어의 독보적 IT 기술력과 전문 상담사를 통해 95% 이상의 예약 성공률을 보장하며, 원하시는 날짜와 시간에 맞춰 최적의 골프장을 추천해 드립니다.',
      },
    },
    {
      '@type': 'Question',
      name: '그린피 할인은 어떻게 적용되나요?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '상품에 따라 그린피 50% 할인 또는 100% 무료 혜택이 제공됩니다. 프리미엄 상품의 경우 그린피 전액이 무료이며, 일반 상품은 4인 기준 그린피의 50%를 할인받으실 수 있습니다.',
      },
    },
  ],
};

function injectSchema(id: string, data: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    (el as HTMLScriptElement).type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    injectSchema('schema-website', schemaWebSite);
    injectSchema('schema-organization', schemaOrganization);
    injectSchema('schema-service', schemaService);
    injectSchema('schema-faq', schemaFAQ);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#hero', label: '홈' },
    { href: '#features', label: '특징' },
    { href: '#benefits', label: '혜택' },
    { href: '#pricing', label: '가격' },
    { href: '#faq', label: 'FAQ' },
    { href: '#signup', label: '상담 신청' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/135a6647eea71ccd89950e580a6d6dcb/07cfc35c21842a86ffee7c15fc8d7e6b.png"
                alt="스마트스코어 멤버십"
                className="h-9 md:h-12 w-auto"
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-300'}`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Mobile Hamburger */}
            <button
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${isScrolled ? 'text-gray-700' : 'text-white'}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="메뉴 열기"
            >
              <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        <div className={`md:hidden bg-white shadow-lg transition-all duration-300 overflow-hidden ${mobileMenuOpen ? 'max-h-96' : 'max-h-0'}`}>
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </nav>

      <Hero />
      <Features />
      <Benefits />
      <Pricing />
      <FAQ />
      <Signup />
    </div>
  );
}
